import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import STORE from 'store';
import _ from 'lodash';

import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import ImpersonatorContext from '../../../../contexts/ImpersonatorContext';
import isDefined from '../../../../isDefined';
import { UIVersionContext } from '../UIVersionProvider';
import { GroupsContext } from '../GroupsProvider';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import RolesContext from '../../../../contexts/RolesContext';
import PERMISSIONS from '../../../../permissionDefinitions';

const useApps = () => {
  const { isV3 } = useContext(UIVersionContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { groupsCurrentUserIsIn } = useContext(GroupsContext);
  const { currentPermissions } = useContext(RolesContext);

  const [products, setProducts] = useState<V3.Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true);

  const [generalApps, setGeneralApps] = useState<V3.GeneralApp[]>([]);
  const [bonusApps, setBonusApps] = useState<V3.BonusApp[]>([]);
  const [legacyBonusApps, setLegacyBonusApps] = useState<V3.LegacyBonusApp[]>(
    [],
  );
  const [workspaces, setWorkspaces] = useState<V3.Workspace[]>([]);
  const [isLoadingGeneralApps, setIsLoadingGeneralApps] =
    useState<boolean>(true);
  const [isLoadingBonusApps, setIsLoadingBonusApps] = useState<boolean>(true);
  const [isLoadingLegacyBonusApps, setIsLoadingLegacyBonusApps] =
    useState<boolean>(true);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState<boolean>(true);

  // Get general apps
  useEffect(() => {
    if (!isV3) {
      setIsLoadingGeneralApps(false);
      setGeneralApps([]);
      return;
    }

    setIsLoadingGeneralApps(true);
    const l = STORE.contentDistributions
      .getGeneralAppsRef({
        accountId: selectedAccountId,
      })
      .onSnapshot((snapshot) => {
        const newApps: V3.GeneralApp[] = [];
        snapshot.forEach((app) => {
          newApps.push(app.data());
        });
        setGeneralApps(newApps);
        setIsLoadingGeneralApps(false);
      });
    return () => {
      l();
    };
  }, [isV3, selectedAccountId]);

  // Get bonus apps
  useEffect(() => {
    if (!isV3) {
      setIsLoadingBonusApps(false);
      setBonusApps([]);
      return;
    }

    setIsLoadingBonusApps(true);
    const l = STORE.contentDistributions
      .getBonusAppsRef({
        accountId: selectedAccountId,
      })
      .onSnapshot((snapshot) => {
        const newApps: V3.BonusApp[] = [];
        snapshot.forEach((app) => {
          newApps.push(app.data());
        });
        setBonusApps(newApps);
        setIsLoadingBonusApps(false);
      });
    return () => {
      l();
    };
  }, [isV3, selectedAccountId]);

  // Get legacy bonus apps
  useEffect(() => {
    if (!isV3) {
      setIsLoadingLegacyBonusApps(false);
      setLegacyBonusApps([]);
      return;
    }

    setIsLoadingLegacyBonusApps(true);
    const l = STORE.contentDistributions
      .getLegacyBonusAppsRef({
        accountId: selectedAccountId,
      })
      .onSnapshot((snapshot) => {
        const newApps: V3.LegacyBonusApp[] = [];
        snapshot.forEach((app) => {
          newApps.push(app.data());
        });
        setLegacyBonusApps(newApps);
        setIsLoadingLegacyBonusApps(false);
      });
    return () => {
      l();
    };
  }, [isV3, selectedAccountId]);

  // Get workspaces
  useEffect(() => {
    if (!isV3) {
      setIsLoadingWorkspaces(false);
      setWorkspaces([]);
      return;
    }

    setIsLoadingWorkspaces(true);
    const l = STORE.contentDistributions
      .getWorkspacesRef({
        accountId: selectedAccountId,
      })
      .onSnapshot((snapshot) => {
        const newApps: V3.Workspace[] = [];
        snapshot.forEach((app) => {
          newApps.push(app.data());
        });
        setWorkspaces(newApps);
        setIsLoadingWorkspaces(false);
      });
    return () => {
      l();
    };
  }, [isV3, selectedAccountId]);

  // Get products
  useEffect(() => {
    if (!isV3) {
      setIsLoadingProducts(false);
      setProducts([]);
      return;
    }

    setIsLoadingProducts(true);
    const l = STORE.contentDistributions
      .getProductsRef({
        accountId: selectedAccountId,
      })
      .onSnapshot((snapshot) => {
        const newProducts: V3.Product[] = [];
        snapshot.forEach((app) => {
          newProducts.push(app.data());
        });
        setProducts(_.sortBy(newProducts, 'label'));

        setIsLoadingProducts(false);
      });
    return () => {
      l();
    };
  }, [isV3, selectedAccountId]);

  const userId = useMemo(() => {
    if (impersonatorId) {
      return impersonatorId;
    }

    return currentUserId;
  }, [currentUserId, impersonatorId]);

  const getHasAccess = useCallback(
    (app: V3.App) => {
      if (!app.isPublished) {
        if (app.createdBy === userId) {
          return true;
        }

        return currentPermissions.includes(
          PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_PRIVATE,
        );
      }

      return (
        app.createdBy === userId ||
        app.adminUserIds.includes(userId) ||
        app.appViewersAccess.isPublic ||
        app.appViewersAccess.userIds.some((uid) => uid === userId) ||
        app.appViewersAccess.groupIds.some(
          (gid) => !!groupsCurrentUserIsIn.find((g) => g.id === gid),
        ) ||
        currentPermissions.includes(
          PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_PRIVATE,
        ) ||
        (app.isPublished &&
          currentPermissions.includes(
            PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_ADMIN_ONLY,
          ))
      );
    },
    [currentPermissions, groupsCurrentUserIsIn, userId],
  );

  const productApps = useMemo(() => {
    return [...generalApps, ...bonusApps, ...legacyBonusApps];
  }, [bonusApps, generalApps, legacyBonusApps]);

  const availableProductApps = useMemo(() => {
    return productApps.filter(getHasAccess);
  }, [getHasAccess, productApps]);

  const availableWorkspaces = useMemo(() => {
    return workspaces.filter(getHasAccess);
  }, [getHasAccess, workspaces]);

  const availableProducts = useMemo(() => {
    return products.filter((product) => {
      if (product.isHidden) {
        return false;
      }

      const accessibleSubApps = product.appIds
        .map((appId) => productApps.find((a) => a.id === appId))
        .filter(isDefined)
        .filter((app) => getHasAccess(app));

      return accessibleSubApps.length >= 1;
    });
  }, [getHasAccess, productApps, products]);

  const reservedWorkspaceUrlSlugs = useMemo(() => {
    return [...workspaces.map((w) => w.urlSlug)];
  }, [workspaces]);

  const reservedProductUrlSlugs = useMemo(() => {
    return [...products.map((p) => p.urlSlug)];
  }, [products]);

  const reservedProductAppUrlSlugs = useMemo(() => {
    return [...productApps.map((a) => a.urlSlug)];
  }, [productApps]);

  return {
    availableProducts,
    availableWorkspaces,
    availableProductApps,
    allWorkspaces: workspaces,
    allProductApps: productApps,
    allProducts: products,
    reservedWorkspaceUrlSlugs,
    reservedProductUrlSlugs,
    reservedProductAppUrlSlugs,
    isLoading:
      isLoadingGeneralApps ||
      isLoadingBonusApps ||
      isLoadingLegacyBonusApps ||
      isLoadingWorkspaces ||
      isLoadingProducts,
    hasAppsEnabled: isV3,
  };
};

export default useApps;
